// featuredProductItem
import axios from "../helpers/axios";
import { featuredProductItemConstants } from "./constants";

const getId = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return value._id || value.id || "";
  }

  return value;
};

const normalizeText = (value) => {
  return String(value || "").toLowerCase().trim();
};

const getProductImage = (product) => {
  if (!product) {
    return "";
  }

  if (product.reducedImage) {
    return product.reducedImage;
  }

  if (Array.isArray(product.productPictures) && product.productPictures.length > 0) {
    const mainPicture = product.productPictures.find((picture) => picture.mainImg || picture.visibleImg);
    return mainPicture?.img || product.productPictures[0]?.img || "";
  }

  return "";
};

const normalizeItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({
    ...item,
    scope: item.scope || "site",
    products: Array.isArray(item.products) ? item.products : [],
  }));
};

const filterItems = (items, additionalData = {}) => {
  const searchName = normalizeText(additionalData.searchName || additionalData.searchMainName);

  if (!searchName) {
    return items;
  }

  return items.filter((item) => {
    const fields = [
      item.name,
      item.type,
      item.scope,
      item.description,
      item.visible ? "видимый" : "скрыт",
      item.visible ? "visible" : "hidden",
    ];

    return fields.some((field) => normalizeText(field).includes(searchName));
  });
};

const sortItems = (items, sort = "createdAtMinus") => {
  const sortedItems = [...items];

  const byString = (field, direction = 1) => {
    sortedItems.sort((a, b) => (
      normalizeText(a[field]).localeCompare(normalizeText(b[field]), "ru") * direction
    ));
  };

  const byDate = (field, direction = 1) => {
    sortedItems.sort((a, b) => {
      const left = new Date(a[field] || 0).getTime();
      const right = new Date(b[field] || 0).getTime();

      return (left - right) * direction;
    });
  };

  const byNumber = (getter, direction = 1) => {
    sortedItems.sort((a, b) => {
      const left = Number(getter(a) || 0);
      const right = Number(getter(b) || 0);

      return (left - right) * direction;
    });
  };

  switch (sort) {
    case "name":
      byString("name", 1);
      break;

    case "nameMinus":
      byString("name", -1);
      break;

    case "type":
      byString("type", 1);
      break;

    case "typeMinus":
      byString("type", -1);
      break;

    case "updatedAt":
      byDate("updatedAt", 1);
      break;

    case "updatedAtMinus":
      byDate("updatedAt", -1);
      break;

    case "createdAt":
      byDate("createdAt", 1);
      break;

    case "createdAtMinus":
      byDate("createdAt", -1);
      break;

    case "products":
      byNumber((item) => item.products?.length, 1);
      break;

    case "productsMinus":
      byNumber((item) => item.products?.length, -1);
      break;

    default:
      byDate("createdAt", -1);
      break;
  }

  return sortedItems;
};

const paginateItems = (items, pageNumber = 0, pageSize = 20) => {
  const currentPageNumber = Number(pageNumber || 0);
  const currentPageSize = Number(pageSize || 20);

  if (!currentPageSize) {
    return items;
  }

  const start = currentPageNumber * currentPageSize;
  const end = start + currentPageSize;

  return items.slice(start, end);
};

const prepareItems = ({
  items,
  sort,
  pageNumber,
  pageSize,
  additionalData,
}) => {
  const normalizedItems = normalizeItems(items);
  const filteredItems = filterItems(normalizedItems, additionalData);
  const sortedItems = sortItems(filteredItems, sort);
  const paginatedItems = paginateItems(sortedItems, pageNumber, pageSize);

  return {
    featuredProductItems: paginatedItems,
    featuredProductItemsAll: sortedItems,
    totalFeaturedProductItems: sortedItems.length,
  };
};

export const getFeaturedProductItemById = (id, random = "false") => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.GET_BY_ID_REQUEST });

      const res = await axios.get(`/featuredProductItem/getById/${id}/${random}`);

      if (res.status === 200) {
        dispatch({
          type: featuredProductItemConstants.GET_BY_ID_SUCCESS,
          payload: res.data,
        });
      } else {
        dispatch({ type: featuredProductItemConstants.GET_BY_ID_FAILURE });
      }
    } catch (error) {
      console.log(error);
      dispatch({ type: featuredProductItemConstants.GET_BY_ID_FAILURE });
    }
  };
};

export const getFeaturedProductItems = (
  sort = "createdAtMinus",
  pageNumber = 0,
  pageSize = 20,
  updatedPictures,
  additionalData = {}
) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.GET_ALL_ITEMS_REQUEST });

      const res = await axios.get("/featuredProductItem/get");

      if (res.status === 200) {
        const { featuredProductItems } = res.data;

        dispatch({
          type: featuredProductItemConstants.GET_ALL_ITEMS_SUCCESS,
          payload: prepareItems({
            items: featuredProductItems,
            sort,
            pageNumber,
            pageSize,
            additionalData,
          }),
        });
      } else {
        dispatch({ type: featuredProductItemConstants.GET_ALL_ITEMS_FAILURE });
      }
    } catch (error) {
      console.log(error);
      dispatch({ type: featuredProductItemConstants.GET_ALL_ITEMS_FAILURE });
    }
  };
};

export const searchFeaturedProductItems = (
  searchName,
  sortType = "createdAtMinus",
  sortSearch,
  additionalData = {}
) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.GET_ALL_ITEMS_REQUEST });

      const res = await axios.get("/featuredProductItem/get");

      if (res.status === 200) {
        const { featuredProductItems } = res.data;

        dispatch({
          type: featuredProductItemConstants.GET_ALL_ITEMS_SUCCESS,
          payload: prepareItems({
            items: featuredProductItems,
            sort: sortType,
            pageNumber: 0,
            pageSize: 20,
            additionalData: {
              ...additionalData,
              searchName,
            },
          }),
        });
      } else {
        dispatch({ type: featuredProductItemConstants.GET_ALL_ITEMS_FAILURE });
      }
    } catch (error) {
      console.log(error);
      dispatch({ type: featuredProductItemConstants.GET_ALL_ITEMS_FAILURE });
    }
  };
};

export const updateFeaturedProductItemById = (id, payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.UPDATE_FEATURED_PRODUCT_ORDER_REQUEST });

      const res = await axios.post(`/featuredProductItem/updateById/${id}`, payload);

      if (res.status === 200) {
        dispatch({
          type: featuredProductItemConstants.UPDATE_FEATURED_PRODUCT_ORDER_SUCCESS,
          payload: res.data
        });

        dispatch(getFeaturedProductItems());
      } else {
        dispatch({
          type: featuredProductItemConstants.UPDATE_FEATURED_PRODUCT_ORDER_FAILURE,
          payload: res.data
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const updateFeaturedProductOrder = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.UPDATE_FEATURED_PRODUCT_ORDER_REQUEST });

      const res = await axios.post("/featuredProductItem/updateProductOrder", payload);

      if (res.status === 200) {
        dispatch({
          type: featuredProductItemConstants.UPDATE_FEATURED_PRODUCT_ORDER_SUCCESS,
          payload: res.data
        });

        if (payload.id) {
          dispatch(getFeaturedProductItemById(payload.id));
        }

        dispatch(getFeaturedProductItems());
      } else {
        dispatch({
          type: featuredProductItemConstants.UPDATE_FEATURED_PRODUCT_ORDER_FAILURE,
          payload: res.data
        });
      }
    } catch (error) {
      console.log(error);

      dispatch({
        type: featuredProductItemConstants.UPDATE_FEATURED_PRODUCT_ORDER_FAILURE,
        payload: error?.response?.data || {
          message: "Ошибка при обновлении списка"
        }
      });
    }
  };
};

export const removeProductByIds = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.REMOVE_PRODUCT_BY_IDS_REQUEST });

      const res = await axios.post("/featuredProductItem/removeProductByIds", payload);

      if (res.status === 200) {
        dispatch({
          type: featuredProductItemConstants.REMOVE_PRODUCT_BY_IDS_SUCCESS,
          payload: res.data
        });
      } else {
        dispatch({
          type: featuredProductItemConstants.REMOVE_PRODUCT_BY_IDS_FAILURE,
          payload: res.data
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const removeProductByIdProductFeaturedProductItem = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.REMOVE_PRODUCT_BY_ID_REQUEST });

      const res = await axios.post("/featuredProductItem/removeProduct", payload);

      if (res.status === 200) {
        dispatch({
          type: featuredProductItemConstants.REMOVE_PRODUCT_BY_ID_SUCCESS,
          payload: res.data
        });
      } else {
        dispatch({
          type: featuredProductItemConstants.REMOVE_PRODUCT_BY_ID_FAILURE,
          payload: res.data
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const addNewProductFeaturedProductItem = (payload) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.ADD_NEW_PRODUCT_REQUEST });

      const normalizedPayload = {
        ...payload,
        newProducts: Array.isArray(payload.newProducts)
          ? payload.newProducts.map((item, index) => ({
            ...item,
            productId: getId(item.productId),
            img: item.img || item.reducedImage || "",
            reducedImage: item.reducedImage || item.img || "",
            order: Number(item.order || index + 1),
          }))
          : [],
      };

      const res = await axios.post("/featuredProductItem/addNewProduct", normalizedPayload);

      if (res.status === 201) {
        dispatch({
          type: featuredProductItemConstants.ADD_NEW_PRODUCT_SUCCESS,
          payload: res.data
        });

        dispatch(getFeaturedProductItemById(payload.id));
        dispatch(getFeaturedProductItems());
      } else {
        dispatch({
          type: featuredProductItemConstants.ADD_NEW_PRODUCT_FAILURE,
          payload: res.data
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const createFeaturedProductItem = (form) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.CREATE_REQUEST });

      const payload = {
        ...form,
        products: Array.isArray(form.products)
          ? form.products.map((item, index) => ({
            ...item,
            productId: getId(item.productId),
            img: item.img || item.reducedImage || "",
            reducedImage: item.reducedImage || item.img || "",
            order: Number(item.order || index + 1),
          }))
          : [],
      };

      const res = await axios.post("/featuredProductItem/create", payload);

      if (res.status === 201) {
        dispatch({
          type: featuredProductItemConstants.CREATE_SUCCESS,
          payload: res.data
        });

        dispatch(getFeaturedProductItems());
      } else {
        dispatch({
          type: featuredProductItemConstants.CREATE_FAILURE,
          payload: res.data
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const deleteFeaturedProductItemById = (id) => {
  return async (dispatch) => {
    try {
      dispatch({ type: featuredProductItemConstants.DELETE_BY_ID_REQUEST });

      const res = await axios.delete(`/featuredProductItem/delete/${id}`);

      if (res.status === 202) {
        dispatch({
          type: featuredProductItemConstants.DELETE_BY_ID_SUCCESS,
          payload: res.data
        });

        dispatch(getFeaturedProductItems());
      } else {
        dispatch({
          type: featuredProductItemConstants.DELETE_BY_ID_FAILURE,
          payload: res.data
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const updateFeaturedProductItemVisibleById = (id) => async (dispatch) => {
  try {
    dispatch({ type: featuredProductItemConstants.UPDATE_VISIBLE_BY_ID_REQUEST });

    const res = await axios.get(`/featuredProductItem/visible/${id}`);

    if (res.status === 200) {
      dispatch({
        type: featuredProductItemConstants.UPDATE_VISIBLE_BY_ID_SUCCESS,
        payload: res.data
      });
    } else {
      dispatch({
        type: featuredProductItemConstants.UPDATE_VISIBLE_BY_ID_FAILURE,
        payload: res.data
      });
    }

    dispatch(getFeaturedProductItems());
  } catch (error) {
    console.log(error);
  }
};